<div class="analytics-container">
  <!-- Reusable template for chart states -->
  <ng-template #chartStates let-isLoading="isLoading" let-hasData="hasData" let-error="error" let-noDataMessage="noDataMessage">
    <div *ngIf="isLoading" class="loading-message">
      <i class="pi pi-spin pi-spinner"></i> Loading data...
    </div>
    <div *ngIf="!isLoading && !hasData && !error" class="no-data-message">
      <i class="pi pi-info-circle"></i> {{ noDataMessage || 'No data available yet' }}
    </div>
    <div *ngIf="error" class="error-message">
      <i class="pi pi-exclamation-triangle"></i> {{ error }}
    </div>
  </ng-template>

  <!-- Header Section matching domes-list style -->
  <div class="header-section">
    <button pButton pRipple icon="pi pi-arrow-left" label="Back to Projects" class="p-button-text" (click)="goBack()" aria-label="Back to projects"></button>
    <h2>Analytics - {{ projectName }}</h2>
  </div>

  <!-- Summary Cards -->
  <div class="summary-cards">
    <div class="summary-card">
      <div class="summary-icon">
        <i class="pi pi-users"></i>
      </div>
      <div class="summary-content">
        <h3>
          <p-skeleton *ngIf="isLoadingSummary" width="4rem" height="1.5rem"></p-skeleton>
          <span *ngIf="!isLoadingSummary">{{ totalActiveUsers | number }}</span>
        </h3>
        <p>Total Active Users</p>
      </div>
    </div>
    
    <div class="summary-card">
      <div class="summary-icon">
        <i class="pi pi-clock"></i>
      </div>
      <div class="summary-content">
        <h3>
          <p-skeleton *ngIf="isLoadingSummary" width="4rem" height="1.5rem"></p-skeleton>
          <span *ngIf="!isLoadingSummary">{{ averageEngagementTime }}</span>
        </h3>
        <p>Avg Engagement Time</p>
      </div>
    </div>
    
    <div class="summary-card">
      <div class="summary-icon">
        <i class="pi pi-clock"></i>
      </div>
      <div class="summary-content">
        <h3>
          <p-skeleton *ngIf="isLoadingSummary" width="4rem" height="1.5rem"></p-skeleton>
          <span *ngIf="!isLoadingSummary">{{ totalSessionDuration }}</span>
        </h3>
        <p>Total Session Duration</p>
      </div>
    </div>
    
    <div class="summary-card">
      <div class="summary-icon">
        <i class="pi pi-mobile"></i>
      </div>
      <div class="summary-content">
        <h3>
          <p-skeleton *ngIf="isLoadingSummary" width="4rem" height="1.5rem"></p-skeleton>
          <span *ngIf="!isLoadingSummary">{{ totalAppLoads | number }}</span>
        </h3>
        <p>Total App Loads</p>
      </div>
    </div>
  </div>

  <!-- Charts Grid -->
  <div class="charts-grid">
    <!-- Experience Load Heatmap -->
    <div class="chart-card full-width">
      <div class="chart-header">
        <div class="header-content">
          <h3>Experience Load Heatmap</h3>
          <p>Geographic distribution of experience opens based on user location data</p>
        </div>
        
        <!-- GPS Executions Counter -->
        <div class="gps-executions-counter">
          <div class="counter-label">GPS Executions</div>
          <div class="counter-value">
            <p-skeleton *ngIf="isLoadingHeatmap" width="3rem" height="1.5rem"></p-skeleton>
            <span *ngIf="!isLoadingHeatmap">{{ totalLocationPoints | number }}</span>
          </div>
        </div>
      </div>
      <div class="chart-container heatmap-container">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingHeatmap,
          hasData: !isLoadingHeatmap && !heatmapError && hasLocationData,
          error: heatmapError,
          noDataMessage: 'No experience location data available yet. Location data will appear here when users open experiences with location tracking enabled.'
        }"></ng-container>
        
        <google-map
          *ngIf="!isLoadingHeatmap && !heatmapError && hasLocationData"
          [options]="mapOptions"
          [width]="'100%'"
          [height]="'100%'"
          (mapInitialized)="onMapReady()">
        </google-map>
      </div>
    </div>

    <!-- User Activity Over Time -->
    <div class="chart-card wide-chart">
      <div class="chart-header">
        <div class="header-content">
          <h3>User Activity Over Time</h3>
          <p>Monthly active users trend</p>
        </div>
      </div>
      <div class="chart-container extra-short">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: userActivityData,
          error: loadingError
        }"></ng-container>
        <p-chart *ngIf="!isLoadingCharts && userActivityData" type="line" [data]="userActivityData" [options]="userActivityOptions"></p-chart>
      </div>
    </div>


    <!-- Experience Loaded Over Time -->
    <div class="chart-card wide-chart">
      <div class="chart-header">
        <div class="header-content">
          <h3>Experience Loaded Over Time</h3>
          <p>Experience loading events by experience name</p>
        </div>
      </div>
      <div class="chart-container extra-short">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: experienceLoadedData,
          error: loadingError,
          noDataMessage: 'No experience loaded events recorded yet'
        }"></ng-container>
        <p-chart *ngIf="!isLoadingCharts && experienceLoadedData" type="line" [data]="experienceLoadedData" [options]="experienceLoadedOptions"></p-chart>
      </div>
    </div>

    <!-- Session Engagement Distribution -->
    <div class="chart-card narrow-chart">
      <div class="chart-header">
        <div class="header-content">
          <h3>Session Engagement Distribution</h3>
          <p>Total Duration: {{ totalSessionDuration }}</p>
        </div>
      </div>
      <div class="chart-container">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: !isLoadingCharts && !loadingError,
          error: loadingError
        }"></ng-container>
        <div *ngIf="!isLoadingCharts && !loadingError" class="engagement-details">
          <div class="engagement-metric">
            <span class="label">High Engagement (5+ sessions)</span>
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="sessionEngagementDistribution.highEngagement"></div>
            </div>
            <span class="percentage">{{ sessionEngagementDistribution.highEngagement }}%</span>
          </div>
          <div class="engagement-metric">
            <span class="label">Medium Engagement (2-4 sessions)</span>
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="sessionEngagementDistribution.mediumEngagement"></div>
            </div>
            <span class="percentage">{{ sessionEngagementDistribution.mediumEngagement }}%</span>
          </div>
          <div class="engagement-metric">
            <span class="label">Low Engagement (1 session)</span>
            <div class="progress-bar">
              <div class="progress-fill" [style.width.%]="sessionEngagementDistribution.lowEngagement"></div>
            </div>
            <span class="percentage">{{ sessionEngagementDistribution.lowEngagement }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Users by App Version -->
    <div class="chart-card">
      <div class="chart-header">
        <div class="header-content">
          <h3>Users by App Version</h3>
          <p>Distribution across app versions</p>
        </div>
      </div>
      <div class="chart-container pie-chart">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: appVersionData,
          error: loadingError
        }"></ng-container>
        <p-chart *ngIf="!isLoadingCharts && appVersionData" type="pie" [data]="appVersionData" [options]="appVersionOptions"></p-chart>
      </div>
    </div>

        <!-- Active Users by Device Model -->
    <div class="chart-card">
      <div class="chart-header">
        <div class="header-content">
          <h3>Active Users by Device Model</h3>
          <p>Device usage distribution</p>
        </div>
        <button
          *ngIf="!isLoadingCharts && allDevicesData.length > 10"
          pButton
          pRipple
          type="button"
          icon="pi pi-list"
          label="View More"
          class="p-button-outlined p-button-sm view-more-button"
          (click)="showAllDevices()"
          aria-label="View all devices">
        </button>
      </div>
      <div class="chart-container tall-chart pie-chart">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: deviceData,
          error: loadingError
        }"></ng-container>
        <p-chart *ngIf="!isLoadingCharts && deviceData" type="pie" [data]="deviceData" [options]="deviceOptions"></p-chart>
      </div>
    </div>


    <!-- Average Engagement Time per Session -->
    <div class="chart-card">
      <div class="chart-header">
        <div class="header-content">
          <h3>Average Engagement Time per Session</h3>
          <p>Session duration: {{ averageSessionTime }}</p>
        </div>
      </div>
      <div class="chart-container">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: !isLoadingCharts && !loadingError,
          error: loadingError
        }"></ng-container>
        <div *ngIf="!isLoadingCharts && !loadingError" class="session-metrics">
          <div class="session-metric">
            <span class="label">Quick Sessions (&lt;1min)</span>
            <span class="value">{{ sessionDurationDistribution.quickSessions }}%</span>
          </div>
          <div class="session-metric">
            <span class="label">Short Sessions (1-3min)</span>
            <span class="value">{{ sessionDurationDistribution.shortSessions }}%</span>
          </div>
          <div class="session-metric">
            <span class="label">Medium Sessions (3-10min)</span>
            <span class="value">{{ sessionDurationDistribution.mediumSessions }}%</span>
          </div>
          <div class="session-metric">
            <span class="label">Long Sessions (10min+)</span>
            <span class="value">{{ sessionDurationDistribution.longSessions }}%</span>
          </div>
        </div>
      </div>
    </div>


    <!-- Active Users by Country -->
    <div class="chart-card">
      <div class="chart-header">
        <div class="header-content">
          <h3>Active Users by Country</h3>
          <p>Geographic distribution</p>
        </div>
      </div>
      <div class="chart-container">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCharts,
          hasData: countryData,
          error: loadingError
        }"></ng-container>
        <p-chart *ngIf="!isLoadingCharts && countryData" type="bar" [data]="countryData" [options]="countryOptions"></p-chart>
      </div>
    </div>

    <!-- CTA Hierarchy Table -->
    <div class="chart-card full-width">
      <div class="chart-header">
        <div class="header-content">
          <h3>CTA Button Clicks by Experience</h3>
          <p>Click-through actions grouped by experience</p>
        </div>
      </div>
      <div class="chart-container table-container">
        <ng-container *ngTemplateOutlet="chartStates; context: {
          isLoading: isLoadingCtaData,
          hasData: ctaHierarchyData && ctaHierarchyData.length > 0,
          error: loadingError,
          noDataMessage: 'No CTA button clicks recorded yet'
        }"></ng-container>
        
        <div *ngIf="!isLoadingCtaData && ctaHierarchyData && ctaHierarchyData.length > 0" class="cta-table-wrapper">
          <p-table
            [value]="ctaHierarchyData"
            styleClass="p-datatable-sm p-datatable-striped">
            
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 3rem"></th>
                <th>Experience</th>
                <th style="width: 8rem; text-align: right">Total Presses</th>
              </tr>
            </ng-template>
            
            <ng-template pTemplate="body" let-experience let-rowIndex="rowIndex">
              <tr class="experience-row" [class.expanded]="isExperienceExpanded(experience.experienceName)">
                <td>
                  <button
                    pButton
                    pRipple
                    type="button"
                    [icon]="isExperienceExpanded(experience.experienceName) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                    class="p-button-text p-button-sm expand-button"
                    (click)="toggleExperienceExpansion(experience.experienceName)"
                    [attr.aria-label]="'Toggle ' + experience.experienceName + ' details'">
                  </button>
                </td>
                <td class="experience-name">
                  <strong>{{ experience.experienceName }}</strong>
                </td>
                <td class="total-presses">
                  <span class="press-count">{{ experience.totalPresses | number }}</span>
                </td>
              </tr>
              
              <!-- Expanded CTA details -->
              <tr *ngIf="isExperienceExpanded(experience.experienceName)" class="expansion-row">
                <td colspan="3" class="expansion-content">
                  <div class="cta-details">
                    <p-table
                      [value]="experience.ctas"
                      styleClass="p-datatable-sm nested-table">
                      
                      <ng-template pTemplate="header">
                        <tr>
                          <th>CTA</th>
                          <th style="width: 8rem; text-align: right">Presses</th>
                          <th style="width: 10rem; text-align: right">% of Experience</th>
                        </tr>
                      </ng-template>
                      
                      <ng-template pTemplate="body" let-cta>
                        <tr>
                          <td class="cta-name">{{ cta.ctaName }}</td>
                          <td class="cta-presses">{{ cta.presses | number }}</td>
                          <td class="cta-percentage">
                            <div class="percentage-container">
                              <span class="percentage-text">{{ cta.percentageOfExperience }}%</span>
                              <div class="percentage-bar">
                                <div class="percentage-fill" [style.width.%]="cta.percentageOfExperience"></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </ng-template>
                      
                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="3" class="empty-message">
                            <i class="pi pi-info-circle"></i>
                            No CTA data available for this experience
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </div>
                </td>
              </tr>
            </ng-template>
            
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3" class="empty-message">
                  <i class="pi pi-info-circle"></i>
                  No CTA button clicks recorded yet
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </div>
    </div>

  </div>

  <!-- Devices Modal Dialog -->
  <p-dialog
    [(visible)]="showDevicesSidebar"
    [modal]="true"
    [style]="{width: '600px', maxHeight: '80vh'}"
    [draggable]="false"
    [resizable]="false"
    header="All Device Models"
    appendTo="body"
    (onHide)="hideDevicesSidebar()">
    
    <div class="devices-modal-content">
      <p class="modal-description">Complete list of devices by active users</p>
      
      <p-table
        [value]="allDevicesData"
        styleClass="p-datatable-sm p-datatable-striped"
        [scrollable]="true"
        scrollHeight="400px">
        
        <ng-template pTemplate="header">
          <tr>
            <th>Device Model</th>
            <th style="width: 5rem; text-align: right">%</th>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="body" let-device>
          <tr>
            <td>{{ device.deviceModel }}</td>
            <td style="text-align: right">{{ device.percentage }}%</td>
          </tr>
        </ng-template>
        
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="2" class="empty-message">
              <i class="pi pi-info-circle"></i>
              No device data available
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </p-dialog>
</div>